"use client";

import { CalendarDays, Clock, MapPin, Banknote } from "lucide-react";
import { quoteThemeCssVars } from "../quote-theme/quote-theme-css-vars";
import { iAttractionTicketLegDetails } from "@/@sdk/holiday-quotes-x/holiday-quotes-x-model";
import { ImageGallery } from "@/components/common/image-gallery/image-gallery";
import { HtmlContentWithReadMore } from "@/components/common/html-content-with-read-more";
import { formatConsistentDate } from "@/utils/timezone-date-utils";

export interface AttractionTicketItineraryInnerProps
  extends iAttractionTicketLegDetails {
  primaryColor?: string;
  day?: number;
}

export function AttractionTicketItineraryInner(
  props: AttractionTicketItineraryInnerProps
) {
  const primaryColor = quoteThemeCssVars.primary;
  const lightColor = quoteThemeCssVars.primary10;

  const formatDate = (date: string | null) => {
    if (!date) return "";
    return formatConsistentDate(date, "EEE, d MMM yyyy");
  };

  const formatCurrency = (price: number, currency: string) => {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: currency,
    }).format(price);
  };

  return (
    <div className="p-4 sm:p-6">
      {/* Attraction Header */}
      {props.attractionName && (
        <div className="mb-4 sm:mb-6">
          <h3 className="text-lg sm:text-xl font-semibold mb-2">
            {props.attractionName}
          </h3>
          {props.ticketType && (
            <span
              className="inline-flex items-center px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm"
              style={{
                backgroundColor: lightColor,
                color: primaryColor,
              }}
            >
              {props.ticketType}
            </span>
          )}
        </div>
      )}

      {/* Overview/Body */}
      {props.body && (
        <HtmlContentWithReadMore
          html={props.body}
          className="mb-4 sm:mb-6"
          maxWords={40}
        />
      )}

      {/* Gallery */}
      {props.gallery && props.gallery.length > 0 && (
        <div className="mb-4 sm:mb-6">
          <ImageGallery images={props.gallery} />
        </div>
      )}

      {/* Ticket Details */}
      <div className="space-y-4 sm:space-y-6">
        {props.date && (
          <div className="flex items-start gap-2 sm:gap-3">
            <CalendarDays
              className="w-4 h-4 sm:w-5 sm:h-5 mt-1"
              style={{ color: primaryColor }}
            />
            <div>
              <div className="font-medium text-sm sm:text-base">
                <span className="hidden sm:inline">Visit Date</span>
                <span className="sm:hidden">Date</span>
              </div>
              <div className="text-sm sm:text-base">{formatDate(props.date)}</div>
              {props.time && (
                <div className="flex items-center gap-1 text-sm text-gray-600 mt-1">
                  <Clock
                    className="w-3 h-3 sm:w-4 sm:h-4"
                    style={{ color: primaryColor }}
                  />
                  <span className="hidden sm:inline">Entry at </span>
                  <span className="sm:hidden">Entry: </span>
                  {props.time}
                </div>
              )}
            </div>
          </div>
        )}

        {props.location && (
          <div className="flex items-start gap-2 sm:gap-3">
            <MapPin
              className="w-4 h-4 sm:w-5 sm:h-5 mt-1"
              style={{ color: primaryColor }}
            />
            <div>
              <div className="font-medium text-sm sm:text-base">
                <span className="hidden sm:inline">Location</span>
                <span className="sm:hidden">Where</span>
              </div>
              <div className="text-sm text-gray-600">{props.location}</div>
            </div>
          </div>
        )}

        {props.price != null && props.price !== 0 && (
          <div className="flex items-start gap-2 sm:gap-3">
            <Banknote
              className="w-4 h-4 sm:w-5 sm:h-5 mt-1"
              style={{ color: primaryColor }}
            />
            <div>
              <div className="font-medium text-sm sm:text-base">
                <span className="hidden sm:inline">Ticket Price</span>
                <span className="sm:hidden">Price</span>
              </div>
              <div className="text-sm text-gray-600">
                {formatCurrency(props.price, props.currency)}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
